import { state, type ClippingState, type FilterState } from "../state";
import { camera, controls } from "../viewer/scene";
import { applyClipping, syncClippingUiFromState } from "./clipping";
import { exitPlanView } from "./planView";
import { rebuildMeshVisibility } from "../ifc/filters";
import { setStatus } from "../ui/status";
import { dom } from "../dom";

/**
 * Share-link через URL hash. Кодируем минимальное состояние:
 *   - камера (position/target/up)
 *   - скрытые expressIds
 *   - clipping state
 *   - filters
 *
 * Структура:
 *   #share=<base64url(json)>
 *
 * Это не «полное состояние приложения», а быстрый способ передать коллеге
 * «открой этот IFC и посмотри сюда». IFC всё ещё нужно загрузить самому
 * (либо открыть из того же облака — мы не передаём содержимое файла).
 */
interface ShareLinkPayload {
  v: 1;
  cam: {
    p: [number, number, number];
    t: [number, number, number];
    u: [number, number, number];
  };
  h?: number[];
  clip?: ClippingState;
  fil?: FilterState;
}

function base64UrlFromString(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function stringFromBase64Url(s: string): string {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return new TextDecoder("utf-8").decode(bytes);
}

function buildPayload(): ShareLinkPayload {
  const payload: ShareLinkPayload = {
    v: 1,
    cam: {
      p: [
        Number(camera.position.x.toFixed(4)),
        Number(camera.position.y.toFixed(4)),
        Number(camera.position.z.toFixed(4)),
      ],
      t: [
        Number(controls.target.x.toFixed(4)),
        Number(controls.target.y.toFixed(4)),
        Number(controls.target.z.toFixed(4)),
      ],
      u: [
        Number(camera.up.x.toFixed(4)),
        Number(camera.up.y.toFixed(4)),
        Number(camera.up.z.toFixed(4)),
      ],
    },
  };
  if (state.hiddenExpressIds.size > 0) {
    payload.h = [...state.hiddenExpressIds];
  }
  if (state.clipping.enabled) {
    payload.clip = JSON.parse(JSON.stringify(state.clipping));
  }
  if (state.activeFilters.storeyId || state.activeFilters.types.length > 0 || state.activeFilters.mark) {
    payload.fil = JSON.parse(JSON.stringify(state.activeFilters));
  }
  return payload;
}

/**
 * Возвращает абсолютный URL текущей страницы с закодированным состоянием.
 * Не модифицирует адресную строку браузера.
 */
export function buildShareLink(): string {
  const payload = buildPayload();
  const encoded = base64UrlFromString(JSON.stringify(payload));
  const baseUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  return `${baseUrl}#share=${encoded}`;
}

/**
 * Копирует share-link в буфер обмена, обновляет адресную строку и
 * показывает ссылку в поле ввода рядом с кнопкой.
 */
export async function copyShareLink(): Promise<void> {
  if (!state.ifcModel) {
    setStatus("Сначала откройте IFC, чтобы создать ссылку.", true);
    return;
  }
  const link = buildShareLink();
  window.history.replaceState(null, "", link.slice(window.location.origin.length));

  dom.shareLinkUrlInput.value = link;
  dom.shareLinkCopyBtn.disabled = false;

  try {
    await navigator.clipboard.writeText(link);
    setStatus("Share-link скопирован в буфер обмена.");
  } catch (_) {
    setStatus("Share-link сгенерирован — нажмите 📋 для копирования.");
  }
}

/**
 * Копирует уже сгенерированный share-link из поля ввода в буфер обмена.
 */
export async function copyShareLinkFromInput(): Promise<void> {
  const link = dom.shareLinkUrlInput.value;
  if (!link) {
    setStatus("Сначала нажмите «Share-link» для генерации ссылки.", true);
    return;
  }
  try {
    await navigator.clipboard.writeText(link);
    setStatus("Share-link скопирован в буфер обмена.");
  } catch (_) {
    setStatus(`Share-link: ${link.length < 200 ? link : link.slice(0, 200) + "…"}`);
  }
}

/**
 * Пробует прочитать share-state из location.hash и применить его. Возвращает
 * `true`, если что-то применено. Должно вызываться **после** того, как
 * пользователь открыл IFC — иначе hidden/clipping применить некуда.
 */
export function tryApplyShareLinkFromLocationHash(): boolean {
  if (!window.location.hash) {
    return false;
  }
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  const params = new URLSearchParams(hash);
  const encoded = params.get("share");
  if (!encoded) {
    return false;
  }
  let payload: ShareLinkPayload;
  try {
    payload = JSON.parse(stringFromBase64Url(encoded)) as ShareLinkPayload;
  } catch (_) {
    setStatus("Share-link: не удалось расшифровать параметры.", true);
    return false;
  }
  if (!payload || payload.v !== 1 || !payload.cam) {
    setStatus("Share-link: неподдерживаемый формат.", true);
    return false;
  }

  if (!state.ifcModel) {
    setStatus("Share-link обнаружен: откройте IFC, чтобы применить ракурс.");
    return false;
  }

  exitPlanView({ restoreVisibility: false });
  camera.position.set(payload.cam.p[0], payload.cam.p[1], payload.cam.p[2]);
  camera.up.set(payload.cam.u[0], payload.cam.u[1], payload.cam.u[2]);
  controls.target.set(payload.cam.t[0], payload.cam.t[1], payload.cam.t[2]);
  controls.update();

  if (payload.h && Array.isArray(payload.h)) {
    state.hiddenExpressIds.clear();
    for (const id of payload.h) {
      if (Number.isFinite(id)) {
        state.hiddenExpressIds.add(Number(id));
      }
    }
  }

  if (payload.clip) {
    state.clipping = payload.clip;
    syncClippingUiFromState();
    applyClipping();
  }

  if (payload.fil) {
    state.activeFilters = {
      storeyId: payload.fil.storeyId || "",
      types: [...(payload.fil.types || [])],
      mark: payload.fil.mark || "",
    };
    dom.filterStoreySelect.value = state.activeFilters.storeyId;
    const typesSet = new Set(state.activeFilters.types);
    [...dom.filterTypeSelect.options].forEach((opt) => {
      opt.selected = typesSet.has(opt.value);
    });
    dom.filterMarkInput.value = state.activeFilters.mark;
  }

  rebuildMeshVisibility();
  setStatus("Применена ссылка-сейчас (share-link).");
  return true;
}
